import React, { useMemo } from "react";
import translations from "./content-de.json";
import { useLocale, Language } from "./index";
import { localizeCharacterBookLinks } from "./character-book-editions";

const dictionary: Record<string, string> = translations;

// These translations are for display only: routing and API actions keep their original values.
export function translateContent(source: string, language: Language): string {
  if (language !== "de" || !source) return source;
  const text = source.trim();
  const exact = dictionary[text];
  if (typeof exact === "string") return exact;
  if (text.startsWith("- ")) return `- ${translateContent(text.slice(2), language)}`;
  const readChapter = text.match(/^Click here to read Chapter (\d+)$/);
  if (readChapter) return `Lies hier Kapitel ${readChapter[1]}`;
  const scene = text.match(/^Just finished reading a steamy scene from ['‘](.+?)['’]?$/);
  if (scene) return `Ich habe gerade eine heiße Szene aus „${scene[1]}“ gelesen`;
  const transcript = text.match(/^(3\.[123]) - (\(MF\) - )?(Quin|Nero|Titus)’s - What If – transcript$/);
  if (transcript) return `${transcript[1]} – ${transcript[2] || ""}${transcript[3]}: „Was wäre, wenn“ – Transkript`;
  const portsmith = text.match(/^(5\.[123]) - (\(MF\) - )?Portsmith – What do you know about it\?$/);
  if (portsmith) return `${portsmith[1]} – ${portsmith[2] || ""}Portsmith – Was weißt du darüber?`;
  const clerk = text.match(/^(6\.[12]) (?:- (\(MF\)) )?– County Clerk’s Office$/);
  if (clerk) return `${clerk[1]} – ${clerk[2] ? "(MF) – " : ""}Das Amt des County Clerk`;
  // Translate edition/navigation labels, not unverified published book or series titles.
  return text.replace(/\bBook (\d+)\b/g, "Band $1")
    .replace(/^Chapter (\d+)(?= -|$)/, "Kapitel $1")
    .replace(/ - Chapter (\d+)/g, " – Kapitel $1")
    .replace(/ - Full Novel$/g, " – Vollständiger Roman")
    .replace(/ - Description$/g, " – Beschreibung")
    .replace(/ - Sneak Peek$/g, " – Leseprobe")
    .replace(/ - Epilogue/g, " – Epilog")
    .replace(/ - Prequel$/g, " – Vorgeschichte")
    .replace(/ - Alternate Sex Scene$/g, " – Alternative Sexszene")
    .replace(/ - Sex Scene$/g, " – Sexszene")
    .replace(/ - Baby News(?= –| -|$)/g, " – Babynachrichten");
}

export function C({ text }: { text?: string }) {
  const { language } = useLocale();
  return <>{translateContent(text || "", language)}</>;
}

export function CountryName({ code, name }: { code: string; name: string }) {
  const { language } = useLocale();
  if (language === "en") return <>{name}</>;
  try {
    const names = new (Intl as any).DisplayNames(["de"], { type: "region" });
    return <>{names.of(code.toUpperCase()) || name}</>;
  } catch { return <>{name}</>; }
}

export function translateDescription(html: string, language: Language): string {
  if (language !== "de" || !html) return html;
  const document = new DOMParser().parseFromString(html, "text/html");
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (["SCRIPT", "STYLE"].includes(node.parentElement?.tagName || "")) continue;
    const source = node.textContent || "";
    if (source.trim()) node.textContent = source.replace(source.trim(), translateContent(source.trim(), language));
  }
  localizeCharacterBookLinks(document);
  return document.body.innerHTML;
}

export function CharacterDescription({ html, className }: { html?: string; className?: string }) {
  const { language } = useLocale();
  const translated = useMemo(() => translateDescription(html || "", language), [html, language]);
  return <p className={className} dangerouslySetInnerHTML={{ __html: translated }} />;
}
